import { Plus, Trash } from 'lucide-react';
import type { KeyValueRow } from '@/lib/key-value';
import { emptyKeyValueRow } from '@/lib/key-value';
import { Button } from './Button';
import { Input } from './Input';

/**
 * Editor de pares clave/valor (campos JSON libres de la API).
 *
 * Se usa en los dos sitios donde el backend acepta un objeto sin esquema fijo: los `datos`
 * de un método de pago y las credenciales libres de un canal (lo que usará TikTok cuando se integre
 * su API; hoy ningún canal del panel lo usa). Cada fila es una clave y su valor, y
 * hay un botón para agregar/eliminar filas. Las filas sin clave se descartan al enviar
 * (`recordFromKeyValueRows`), así que no hay que "limpiar" a mano.
 *
 * Componente controlado: el estado (las filas) lo tiene el formulario que lo usa.
 */

export interface KeyValueFieldsProps {
  rows: KeyValueRow[];
  onChange: (rows: KeyValueRow[]) => void;
  /** Etiquetas de las dos columnas (por defecto "Clave" / "Valor"). */
  keyLabel?: string;
  valueLabel?: string;
  addLabel?: string;
  /** Ejemplos para el placeholder (`["cuenta", "titular"]`). */
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  disabled?: boolean;
  className?: string;
}

export function KeyValueFields({
  rows,
  onChange,
  keyLabel = 'Clave',
  valueLabel = 'Valor',
  addLabel = 'Agregar campo',
  keyPlaceholder = 'ej. cuenta',
  valuePlaceholder = 'ej. 3001234567',
  disabled = false,
  className,
}: KeyValueFieldsProps) {
  function updateRow(id: string, patch: Partial<KeyValueRow>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function removeRow(id: string) {
    const next = rows.filter((row) => row.id !== id);
    onChange(next.length > 0 ? next : [emptyKeyValueRow()]);
  }

  return (
    <div className={className}>
      <div className="mb-1.5 flex items-center gap-3">
        <span className="flex-1 text-caption font-medium text-ink-soft">{keyLabel}</span>
        <span className="flex-1 text-caption font-medium text-ink-soft">{valueLabel}</span>
        {/* Espacio del botón de eliminar, para que las dos columnas queden alineadas. */}
        <span className="w-9" aria-hidden="true" />
      </div>

      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.id} className="flex items-center gap-3">
            <Input
              value={row.key}
              placeholder={keyPlaceholder}
              disabled={disabled}
              aria-label={keyLabel}
              onChange={(event) => updateRow(row.id, { key: event.target.value })}
            />

            <Input
              value={row.value}
              placeholder={valuePlaceholder}
              disabled={disabled}
              aria-label={valueLabel}
              onChange={(event) => updateRow(row.id, { value: event.target.value })}
            />

            <Button
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label={`Eliminar el campo ${row.key || 'vacío'}`}
              onClick={() => removeRow(row.id)}
            >
              <Trash size={16} />
            </Button>
          </div>
        ))}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        disabled={disabled}
        leftIcon={<Plus size={14} />}
        onClick={() => onChange([...rows, emptyKeyValueRow()])}
      >
        {addLabel}
      </Button>
    </div>
  );
}
